import { Breadcrumbs, Stack, Typography } from '@mui/material';
import { MessageSender, MessageToApprove } from 'features/co-pilot/co-pilot.types';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiTextArea from 'features/ui/base-components/pai-text-area';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  message: MessageToApprove | undefined;
  sender: MessageSender | null;
  messageToEditText: string | null;
  isEditing: boolean;

  handleEditMessage(): void;

  handleCancelEditMessage(): void;

  handleMessageToApproveChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleOpenContactDrawer(url: string, profileId: string, funnelId?: string): () => void;
}

const MessageItem: React.FC<Props> = ({
  message,
  sender,
  messageToEditText,
  isEditing,
  handleEditMessage,
  handleCancelEditMessage,
  handleMessageToApproveChange,
  handleOpenContactDrawer,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const receiver = message?.receiver;
  const senderName =
    !_.isNil(sender?.linkedinAccount?.userFirstName) && !_.isNil(sender?.linkedinAccount?.userLastName)
      ? `${sender.linkedinAccount.userFirstName} ${sender.linkedinAccount.userLastName}`
      : sender?.linkedinAccount?.userEmail;
  const senderFunnel = sender?.funnel?.name;

  return (
    <Stack className={classes.messageItem}>
      {!_.isNil(receiver?.name) || !_.isNil(receiver?.position) || !_.isNil(receiver?.company) ? (
        <Stack className={classes.tableRow} direction={'row'} alignItems={'center'} gap={1}>
          <Typography className={classes.text}>{t('to')}:</Typography>

          <Breadcrumbs>
            {!_.isNil(receiver?.name) ? (
              <PaiLink
                text={receiver.name}
                size={'lg'}
                variant={'default'}
                underline={'always'}
                action={handleOpenContactDrawer(receiver.linkedInURL, receiver.profileId, sender?.funnel?._id)}
              />
            ) : (
              <Typography className={classes.text}>{receiver?.name || t('linkedinUser')}</Typography>
            )}
            {!_.isNil(receiver?.position) ? <Typography className={classes.text}>{receiver.position}</Typography> : null}
            {!_.isNil(receiver?.company) ? <Typography className={classes.text}>{receiver.company}</Typography> : null}
          </Breadcrumbs>
        </Stack>
      ) : null}
      {!_.isNil(senderName) ? (
        <Stack className={classes.tableRow} direction={'row'} alignItems={'center'} gap={1} mb={'8px'}>
          <Typography className={classes.text}>{t('from')}:</Typography>
          <Stack direction={'row'} alignContent={'center'} gap={0.5}>
            <Typography className={cx(classes.text, classes.highlight)}>{senderName}</Typography>
            {!_.isNil(senderFunnel) ? (
              <>
                <Typography className={classes.text}>{t('in')}</Typography>
                <Typography className={cx(classes.text, classes.highlight)}>{senderFunnel}</Typography>
              </>
            ) : null}
          </Stack>
        </Stack>
      ) : null}
      {!_.isNil(message?.subject) ? <Typography className={cx(classes.greyText, classes.tableRow)}>{`${t('subject')}: ${message.subject}`}</Typography> : null}
      {isEditing ? (
        <PaiTextArea value={messageToEditText || ''} rows={0} variant={'background'} onChange={handleMessageToApproveChange} />
      ) : (
        <Typography className={cx(classes.greyText, classes.tableRow)}>{message?.text}</Typography>
      )}
      <Stack className={cx(classes.tableRow, 'actions')} direction={'row'} justifyContent={'flex-end'} gap={2}>
        <Typography className={classes.wordCount}>
          {`${t('characters')}: ${isEditing && !_.isNil(messageToEditText) ? messageToEditText?.length : message?.text?.length}`}
        </Typography>
        {!isEditing ? (
          <>
            <PaiLink text={t('edit')} size={'lg'} variant={'primary'} underline={'always'} textTransform={'lowercase'} action={handleEditMessage} />
          </>
        ) : (
          <>
            <PaiLink
              text={t('cancel')}
              size={'lg'}
              variant={'primary'}
              underline={'always'}
              textTransform={'lowercase'}
              action={handleCancelEditMessage}
            />
          </>
        )}
      </Stack>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  messageItem: {
    position: 'relative',
    width: '100%',
    minHeight: '40px',
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  tableRow: {
    padding: '8px 8px 8px 16px',
    '&.actions': {
      paddingRight: '16px',
      textAlign: 'right',
    },
  },
  wordCount: {
    fontSize: theme.typography.pxToRem(15),
    marginRight: 'auto',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
  },
  highlight: {
    color: theme.palette.primary.main,
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
}));

export default MessageItem;
